// src/seo.js
import { useEffect } from 'react';
import { SITE } from './siteInfo';

/**
 * The public origin, used for canonical links and the sitemap. It has to be
 * absolute and it has to match what Google actually crawls, so it is stated
 * once here rather than derived from window.location — which would happily
 * canonicalise a preview build to localhost.
 */
export const SITE_URL = 'https://crestancia.co.za';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

/**
 * Sets the per-page title, description, canonical and robots directive.
 *
 * Worth being clear about the limit: this runs in JavaScript. Google renders
 * pages before indexing so it sees these, but the crawlers behind link
 * previews — WhatsApp, Facebook, LinkedIn — do not run JS and only ever see
 * the static tags in public/index.html. Fixing that properly means
 * pre-rendering, which is a larger job than this hook.
 *
 * `noindex` is how the cart, checkout and account pages stay out of search
 * results. They are deliberately left crawlable so Google can actually read
 * the directive; a robots.txt Disallow would hide the very tag telling it to
 * drop the page.
 */
export const useSeo = ({ title, description, path, noindex = false }) => {
  useEffect(() => {
    const fullTitle = title || SITE.name;
    const desc = description || SITE.about;
    const canonical = `${SITE_URL}${path || '/'}`;

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', { name: 'description', content: desc });
    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: desc });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: desc });

    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, follow' });
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, path, noindex]);
};

/**
 * Drops a JSON-LD block into the head and takes it away again on unmount, so
 * two pages can never leave two competing descriptions of the same thing.
 */
export const useJsonLd = (id, data) => {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, data]);
};
